import React from "react";
import { useSelector } from "react-redux";
import { Col, Container, Row } from "react-bootstrap";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { STORAGE_URL } from "constants";
import { useFetch, useLanguage } from "hooks";
import { Layout, Skeletons } from "components";

const MySwal = withReactContent(Swal); // Swal is a library for Swal Fire; to use it in React, you should pass it to the withReactContent function.

export default function Contact() {
  const language = useLanguage(); //language returned from useLanguage hook; it’s an object with current language key --> "value in current language";
  const [errors, setErrors] = React.useState({});
  const { socialMediaLinks, settings } = useSelector((state) => state.app); // settings, socialMediaLinks from redux app store that call it's function in app.js
  const [_, contactFormRequest] = useFetch("submitContactForm", "POST"); // useFetch returns contactFormRequest function that call "submitContactForm" api end points in method  "POST"
  const emailInputRef = React.useRef();
  const nameInputRef = React.useRef();
  const phoneInputRef = React.useRef();
  const messageInputRef = React.useRef();
  const lastNameInputRef = React.useRef();

  const handleSubmitContactForm = (e) => {
    e.preventDefault();
    contactFormRequest(
      {
        first_name: nameInputRef.current.value,
        last_name: lastNameInputRef.current.value,
        email: emailInputRef.current.value,
        phone: phoneInputRef.current.value,
        message: messageInputRef.current.value,
      },
      null,
      () => {
        resetInputs();
        return MySwal.fire({
          icon: "success",
          title: language.submit_success,
          text: language.submit_success_message,
          confirmButtonText: "متابعة",
        });
      },
      (error) => {
        setErrors(error);
      }
    );
  }; // on submit the form sent the request and set the data on the body: first_name, last_name, email, phone, message

  const resetInputs = () => {
    emailInputRef.current.value = "";
    nameInputRef.current.value = "";
    phoneInputRef.current.value = "";
    messageInputRef.current.value = "";
    lastNameInputRef.current.value = "";
    setErrors({});
  };

  return (
    <Layout>
      <div id="contact_page" className="page_container pt-5">
        <section className="mb-3">
          <Container>
            <Row className="justify-content-between mt-4 mt-4 mt-4 ">
              {/* Right */}
              <Col xl={5} lg={5} md={12} sm={12} xs={12}>
                <Row>
                  <Col xs={12}>
                    <h1 className="text-primary fw-bold cut-text-2">
                      {language.stayContact}
                    </h1>
                  </Col>
                </Row>
                <Row className="mt-4">
                  <Col xs={12}>
                    {!settings.loading ? (
                      <p
                        className="text-light5 fw-normal"
                        dangerouslySetInnerHTML={{
                          __html: settings.contact_page_text,
                        }}
                      /> // Contact Page Text  in admin setting
                    ) : (
                      <Skeletons.Paragraph />
                    )}
                  </Col>
                </Row>
                <Row className="px-0 mx-0">
                  {/*lg=  10 >>> 12  */}
                  <Col xs={12}>
                    <Row className="px2-4 px-lg-none">
                      <Col
                        xl={6}
                        lg={12}
                        md={3}
                        sm={6}
                        xs={12}
                        className="mt-4"
                      >
                        <p className="fw-bold text-light5">
                          {language.contactNumber}
                        </p>
                      </Col>

                      <Col
                        xl={6}
                        lg={12}
                        md={3}
                        sm={6}
                        xs={12}
                        className="mt-xl-4 mt-lg-2 mt-md-4 mt-sm-4 mt-2"
                      >
                        {!settings.loading ? (
                          <a href={`tel:${settings.phone}`}>
                            <p className="phone_number fw-bold text-light5">
                              {settings.phone}
                            </p>
                          </a> // phone in admin setting
                        ) : (
                          <Skeletons.Paragraph rows={1} />
                        )}
                      </Col>

                      <Col
                        xl={6}
                        lg={12}
                        md={3}
                        sm={6}
                        xs={12}
                        className="mt-4"
                      >
                        <p className="fw-bold text-light5">
                          {language.emailAddress}
                        </p>
                      </Col>

                      <Col
                        xl={6}
                        lg={12}
                        md={3}
                        sm={6}
                        xs={12}
                        className="mt-xl-4 mt-lg-2 mt-md-4 mt-sm-4 mt-2"
                      >
                        {!settings.loading ? (
                          <a href={`mailto:${settings.email}`}>
                            {" "}
                            <p className="fw-bold text-light5">
                              {settings.email}
                            </p>
                          </a> // email in admin setting
                        ) : (
                          <Skeletons.Paragraph rows={1} />
                        )}
                      </Col>
                      {socialMediaLinks?.data?.length > 0 && (
                        <Col sx={12}>
                          <ul className="social_media_links d-flex flex-wrap gap-3 list-unstyled mt-4">
                            {socialMediaLinks.data.map((item) => (
                              <li
                                key={item.id}
                                className="filter_hover d-flex align-items-center bg-transparent border-0 p-0"
                              >
                                <a
                                  href={item.link}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <img
                                    width="42"
                                    src={`${STORAGE_URL}${item.icon}`}
                                    alt={item.name}
                                  />
                                </a>
                              </li>
                            ))}
                          </ul>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
              </Col>
              {/* Left */}
              <Col
                xl={6}
                lg={6}
                md={12}
                sm={12}
                xs={12}
                // className="mt-4 mt-4 mt-4 mt-lg-0"
                className="mt-0"
              >
                <form onSubmit={handleSubmitContactForm}>
                  <Row className="mb-4">
                    <Col xl={6} lg={6} md={6} sm={12} xs={12} className="mt-4">
                      <label
                        htmlFor="first_name"
                        className="form-label text-light5"
                      >
                        {language.firstName}
                      </label>
                      <input
                        ref={nameInputRef}
                        type="text"
                        id="first_name"
                        className={`form-control rounded-1 ${
                          errors.first_name &&
                          errors.first_name[0] &&
                          "border-danger"
                        }`}
                        placeholder={language.firstName}
                      />
                      <div className="text-danger">
                        {errors.first_name && errors.first_name[0]}
                      </div>
                    </Col>
                    <Col xl={6} lg={6} md={6} sm={12} xs={12} className="mt-4">
                      <label
                        htmlFor="last_name"
                        className="form-label text-light5"
                      >
                        {language.secondName}
                      </label>
                      <input
                        ref={lastNameInputRef}
                        type="text"
                        id="last_name"
                        className={`form-control rounded-1 ${
                          errors.last_name &&
                          errors.last_name[0] &&
                          "border-danger"
                        }`}
                        placeholder={language.secondName}
                      />
                      <div className="text-danger">
                        {errors.last_name && errors.last_name[0]}
                      </div>
                    </Col>
                    <Col xl={6} lg={6} md={6} sm={12} xs={12} className="mt-4">
                      <label htmlFor="email" className="form-label text-light5">
                        {language.emailAddress}
                      </label>
                      <input
                        type="email"
                        ref={emailInputRef}
                        id="email"
                        className={`form-control rounded-1 ${
                          errors.email && errors.email[0] && "border-danger"
                        }`}
                        placeholder={language.emailAddress}
                      />
                      <div className="text-danger">
                        {errors.email && errors.email[0]}
                      </div>
                    </Col>
                    <Col xl={6} lg={6} md={6} sm={12} xs={12} className="mt-4">
                      <label
                        htmlFor="phone_number"
                        className="form-label text-light5"
                      >
                        {language.phoneNumber}
                      </label>
                      <input
                        type="tel"
                        ref={phoneInputRef}
                        id="phone_number"
                        className={`form-control rounded-1 ${
                          errors.phone && errors.phone[0] && "border-danger"
                        }`}
                        placeholder={language.phoneNumber}
                      />
                      <div className="text-danger">
                        {errors.phone && errors.phone[0]}
                      </div>
                    </Col>
                    <Col
                      xl={12}
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      className="mt-4"
                    >
                      <label
                        htmlFor="message"
                        className="form-label text-light5"
                      >
                        {language.message}
                      </label>
                      <textarea
                        ref={messageInputRef}
                        className={`form-control w-100 py-3 rounded-1 ${
                          errors.message && errors.message[0] && "border-danger"
                        }`}
                        rows={10}
                      />
                      <div className="text-danger">
                        {" "}
                        {errors.message && errors.message[0]}{" "}
                      </div>
                    </Col>
                    <Col
                      xl={12}
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      className="mt-4"
                    >
                      <button className="btn btn-outline-primary form-control w-50 fw-bold float-start">
                        {language.send}
                      </button>
                    </Col>
                  </Row>
                </form>
                {/*contact form with inputs First Name, Second Name, Email Address, Phone Number and send to submit*/}
              </Col>
            </Row>
          </Container>
        </section>
      </div>
    </Layout>
  );
}
